import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FingerScan } from 'iconsax-react';
import AdminSecurityApi, { errorCode, rateLimitedMessage } from '@infrastructure/admin-security-api';
import { getPasskey, PasskeyCancelled, passkeysSupported } from '@application/common/helpers/webauthn';
import { PrimaryButon } from '../../components/common/button/PrimaryButon';
import { black400, black500 } from '../../constants/color';
// Rebels admin sign-in steps (add-rebels-admin-security-and-mcp-connectors, task R4.1; port of the QPortal live
// frontend). Rendered only when the API capability is on, or after the API asked for a second step.
const messages = {
    SIGN_IN_FAILED: 'That did not work. Check the code and try again.',
    CHALLENGE_INVALID: 'This sign-in attempt expired. Please sign in again.',
    RATE_LIMITED: 'Too many attempts. Please wait and try again later.',
    ADMIN_SECURITY_DISABLED: 'Passkey sign-in is not available right now.'
};
const message = (code) => messages[code] || 'Sign in could not be completed. Please try again.';
// A rate limit can last seconds (endpoint) or up to an hour (per account): show the actual wait.
const messageFor = (error) => (errorCode(error) === 'RATE_LIMITED' ? rateLimitedMessage(error) : message(errorCode(error)));
// One-time code input in the portal's rounded input style, with the right keyboard and autofill on phones. It draws
// its own visible edge and focus ring (the shared inputs are borderless), so it stays visible in dialogs as well as on
// the public pages.
export const CodeField = ({ value, onChange, label, placeholder, numeric }) => (_jsx(TextField, { fullWidth: true, autoFocus: true, value: value, onChange: (e) => onChange(numeric ? e.target.value.replace(/\D/g, '').slice(0, 6) : e.target.value.slice(0, 20)), placeholder: placeholder, inputProps: { 'aria-label': label, inputMode: numeric ? 'numeric' : 'text', autoComplete: 'one-time-code', spellCheck: false }, sx: {
        mt: 2.5,
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#FFFFFF',
            borderRadius: '25px',
            fontSize: 18,
            letterSpacing: numeric ? '0.3em' : '0.05em',
            boxShadow: 'inset 0 0 0 1px #848f9b',
            '& fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: 'none' },
            '&.Mui-focused': { boxShadow: 'inset 0 0 0 2px #960041' }
        }
    } }));
// Discoverable passkey sign-in for Admin/SuperAdmin accounts. Shown only when the browser supports passkeys and the
// API capability is on; while the capability is off (or the API does not know it) nothing is rendered.
export const PasskeySignInButton = ({ onSignedIn, label = 'Sign in with passkey' }) => {
    const [available, setAvailable] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => {
        let alive = true;
        if (passkeysSupported())
            AdminSecurityApi.capability()
                .then((capability) => alive && setAvailable(capability?.enabled === true))
                .catch(() => undefined);
        return () => {
            alive = false;
        };
    }, []);
    if (!available)
        return null;
    const signIn = async () => {
        if (busy)
            return;
        setBusy(true);
        setError('');
        try {
            const { challengeId, options } = await AdminSecurityApi.loginPasskeyOptions();
            const credentialJson = await getPasskey(options);
            onSignedIn((await AdminSecurityApi.loginPasskeyComplete(challengeId, credentialJson)).accessToken);
        }
        catch (e) {
            setError(e instanceof PasskeyCancelled ? 'The passkey request was cancelled. You can try again.' : messageFor(e));
        }
        finally {
            setBusy(false);
        }
    };
    return (_jsxs(Box, { sx: { width: '100%', mt: 2 }, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'secondary', startIcon: _jsx(FingerScan, { size: 20 }), disabled: busy, onClick: signIn, sx: { minHeight: 48, borderRadius: '30px', textTransform: 'none', fontWeight: 500 }, children: busy ? 'Waiting for your passkey…' : label }), error && _jsx(Alert, { severity: 'error', sx: { mt: 1 }, children: error })] }));
};
// Second step after a correct admin password: authenticator-app code or one recovery code.
export const AdminSecondStep = ({ challengeId, passkeyOnly, onSignedIn, onCancel }) => {
    const [code, setCode] = useState('');
    const [useRecovery, setUseRecovery] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const submit = async (event) => {
        event.preventDefault();
        if (busy || !challengeId)
            return;
        setBusy(true);
        setError('');
        try {
            const result = useRecovery
                ? await AdminSecurityApi.secondFactor(challengeId, undefined, code)
                : await AdminSecurityApi.secondFactor(challengeId, code);
            onSignedIn(result.accessToken);
        }
        catch (e) {
            const failure = errorCode(e);
            setError(messageFor(e));
            if (failure === 'CHALLENGE_INVALID')
                setTimeout(onCancel, 1500);
        }
        finally {
            setBusy(false);
        }
    };
    if (passkeyOnly || !challengeId)
        return (_jsxs(Box, { sx: { width: '100%', mt: 3 }, children: [_jsx(Typography, { variant: 'h6', color: black500, children: "Use your passkey" }), _jsx(Typography, { variant: 'body2', color: black400, mt: 1, children: "This admin account signs in with a passkey. Your password alone is not enough." }), _jsx(PasskeySignInButton, { onSignedIn: onSignedIn, label: 'Continue with passkey' }), _jsx(Button, { sx: { mt: 2 }, onClick: onCancel, children: "Back to sign in" })] }));
    return (_jsxs(Box, { component: 'form', onSubmit: submit, sx: { width: '100%', mt: 3 }, noValidate: true, children: [_jsx(Typography, { variant: 'h6', color: black500, children: useRecovery ? 'Enter a recovery code' : 'Enter your authentication code' }), _jsx(Typography, { variant: 'body2', color: black400, mt: 1, children: useRecovery
                    ? 'Each recovery code works once. You received them when you set up your authenticator app.'
                    : 'Open your authenticator app and enter the 6-digit code for Rebels.' }), _jsx(CodeField, { value: code, onChange: setCode, label: useRecovery ? 'Recovery code' : 'Authentication code', placeholder: useRecovery ? 'XXXX-XXXX-XXXX' : '123456', numeric: !useRecovery }), error && _jsx(Alert, { severity: 'error', sx: { mt: 2 }, children: error }), _jsx(PrimaryButon, { type: 'submit', mt: 20, disabled: busy || (useRecovery ? code.trim().length < 12 : code.replace(/\D/g, '').length !== 6), children: busy ? 'Checking…' : 'Verify and sign in' }), _jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 2 }, children: [_jsx(Button, { onClick: () => { setUseRecovery(!useRecovery); setCode(''); setError(''); }, children: useRecovery ? 'Use authenticator app' : 'Use a recovery code' }), _jsx(Button, { onClick: onCancel, children: "Back to sign in" })] })] }));
};
